import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import {
  Image,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuDivider,
  MenuGroup,
  Text,
} from "@chakra-ui/react";

import { authApi, userApi } from "api";
import userIcon from "assets/images/user-icon.svg";
import { MyAccountTab, MyAccountEvents } from "components/MyAccountView/MyAccountView";
import { PAGE_PATHS } from "config/routes";
import { SessionContext, TrackingContext } from "contexts";
import { useTrackEvent } from "utils/tracking/hooks";

const { destroySession } = userApi;
const { authLogout } = authApi;

const UserAction: React.FC = () => {
  const { t } = useTranslation();
  const { login, user } = useContext(SessionContext);
  const { setLoginData, subPageType } = useContext(TrackingContext);
  const { trackCtaClick } = useTrackEvent();

  const myAccountMenuItems = useMemo(() => {
    return [
      {
        eventName: MyAccountEvents.DETAILS,
        key: MyAccountTab.DETAILS,
        label: t("common.details"),
        path: PAGE_PATHS.MY_ACCOUNT.DETAILS_PAGE,
      },
      {
        eventName: MyAccountEvents.BENEFITS,
        key: MyAccountTab.BENEFITS,
        label: t("common.my_benefits"),
        path: PAGE_PATHS.MY_ACCOUNT.BENEFITS_PAGE,
      },
      {
        eventName: MyAccountEvents.SETTINGS,
        key: MyAccountTab.SETTINGS,
        label: t("common.settings"),
        path: PAGE_PATHS.MY_ACCOUNT.SETTINGS_PAGE,
      },
    ];
  }, [t]);

  const logout = async () => {
    await authLogout();
    await destroySession();
    setLoginData(null);
    return login(null);
  };

  return (
    <Menu
      isLazy
      closeOnBlur
      closeOnSelect
      autoSelect={false}
      onOpen={() => {
        trackCtaClick("user menu", subPageType);
      }}
    >
      <MenuButton data-testid="toggle-user-menu">
        <Image width={8} height={8} src={userIcon} alt="" />
      </MenuButton>
      <MenuList minWidth="165px" backgroundColor="greyscale.100" data-testid="user-action-dropdown" py={1} px={1}>
        <MenuGroup
          color="primary.900"
          fontWeight="500"
          fontSize="m"
          title={t("my_account.logged_as")}
          ml={5}
          mt={3}
          mb={1}
        >
          <Text px={5} pb={3} my={0} fontSize="m">
            {user?.email}
          </Text>
        </MenuGroup>
        <MenuDivider mx={-1} my={1} />
        <MenuGroup color="primary.900" fontWeight="500" fontSize="m" title={t("my_account.header")} ml={6} mt={4}>
          {myAccountMenuItems.map((menuItem) => (
            <Link
              role="button"
              to={generatePath(menuItem.path)}
              onClick={() => {
                trackCtaClick(`${menuItem.eventName}`, "user menu");
              }}
              data-testid={`menu-item-${menuItem.key}`}
              key={`menu-item-${menuItem.key}`}
            >
              <MenuItem
                px={6}
                py={2}
                borderRadius="md"
                _hover={{
                  backgroundColor: "greyscale.300",
                }}
              >
                {menuItem.label}
              </MenuItem>
            </Link>
          ))}
        </MenuGroup>
        <MenuDivider mx={-1} my={1} />

        <MenuItem
          as={ChakraLink}
          role="menuitem"
          onClick={() => {
            trackCtaClick("logout", "user menu");
            logout();
          }}
          data-testid="logout-link"
          fontWeight="normal"
          px={6}
          py={2}
          borderRadius="md"
          _hover={{
            backgroundColor: "greyscale.300",
            textDecoration: "none",
            color: "primary.900",
          }}
          _focus={{ boxShadow: "none" }}
        >
          {t("authentication.logout")}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default UserAction;
